import { useState, useRef } from 'react';
import NoPhiNotice from './NoPhiNotice';
import { httpsCallable } from '../lib/callable';
import { Upload, FileText, Loader2, CheckCircle2, AlertTriangle, X } from 'lucide-react';
import { functions } from '../firebase';
import { CLOUD_FUNCTIONS, CALLABLE_TIMEOUTS } from '../constants/firebase';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';

interface UploadDataDictionaryModalProps {
  /** Project that the imported forms attach to. */
  projectId: string;
  /** Called after import succeeds with the form count. */
  onComplete: (formsImported: number) => void;
  /** Called if the user cancels before import starts. */
  onClose: () => void;
}

type Stage = 'picking' | 'reading' | 'importing' | 'done' | 'error';

const ALLOWED_EXTENSIONS = ['.csv'];

export default function UploadDataDictionaryModal({
  projectId,
  onComplete,
  onClose,
}: UploadDataDictionaryModalProps) {
  const { user } = useAuth();
  const [file, setFile] = useState<File | null>(null);
  const [stage, setStage] = useState<Stage>('picking');
  const [warnings, setWarnings] = useState<string[]>([]);
  const [formsImported, setFormsImported] = useState(0);
  const [variablesImported, setVariablesImported] = useState(0);
  const [error, setError] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const { showToast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelected = (picked: File) => {
    const lower = picked.name.toLowerCase();
    const validExt = ALLOWED_EXTENSIONS.some((ext) => lower.endsWith(ext));
    if (!validExt) {
      showToast('Only .csv files are supported (REDCap data dictionary export)', 'error');
      return;
    }
    setFile(picked);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    const f = e.dataTransfer.files?.[0];
    if (f) handleFileSelected(f);
  };

  const run = async () => {
    if (!user) {
      showToast('You must be logged in.', 'error');
      return;
    }
    if (!file) return;
    setError('');
    setWarnings([]);
    try {
      setStage('reading');
      // CSVs are tiny (a few KB to tens of KB) so we read the whole
      // text into memory and ship it directly to the callable. No
      // Storage round-trip needed.
      const csvText = await file.text();

      setStage('importing');
      const callable = httpsCallable(
        functions,
        CLOUD_FUNCTIONS.IMPORT_REDCAP_DATA_DICTIONARY,
        { timeout: CALLABLE_TIMEOUTS.MEDIUM },
      );
      const result = await callable({ projectId, csvText });
      const data = result.data as {
        formsImported: number;
        variablesImported: number;
        warnings: string[];
      };
      setFormsImported(data.formsImported || 0);
      setVariablesImported(data.variablesImported || 0);
      setWarnings(data.warnings || []);
      if (!data.formsImported) {
        setStage('error');
        setError(
          'No forms were created. Check that the CSV is a REDCap data dictionary export and has at least one valid row.',
        );
        return;
      }
      setStage('done');
      onComplete(data.formsImported);
    } catch (err: any) {
      console.error('[UploadDataDictionaryModal] failed:', err);
      const code: string | undefined = err?.code;
      const rawMessage: string | undefined = err?.message;
      const display =
        rawMessage && rawMessage !== code && rawMessage !== 'internal'
          ? rawMessage
          : code
            ? `${code}${rawMessage ? ` — ${rawMessage}` : ''}`
            : 'Import failed';
      setStage('error');
      setError(display);
    }
  };

  const busy = stage === 'reading' || stage === 'importing';

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Import Data Dictionary</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Bring CRFs into this project from a REDCap DD export (.csv).
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={busy}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 min-h-[220px]">
          {stage === 'picking' && (
            <>
            <NoPhiNotice className="mb-4" />
            <div
              onDragEnter={(e) => { e.preventDefault(); setDragActive(true); }}
              onDragLeave={() => setDragActive(false)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                dragActive ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:border-gray-300'
              }`}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleFileSelected(f);
                }}
              />
              {file ? (
                <div className="flex flex-col items-center">
                  <FileText className="h-8 w-8 text-gray-400 mb-2" />
                  <p className="text-sm font-medium text-gray-700">{file.name}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    {Math.round(file.size / 1024)} KB
                  </p>
                </div>
              ) : (
                <div className="flex flex-col items-center text-gray-500">
                  <Upload className="h-8 w-8 mb-2" />
                  <p className="text-sm font-medium text-gray-700">Drop a REDCap DD CSV here</p>
                  <p className="text-xs text-gray-400 mt-1">or click to choose a file</p>
                </div>
              )}
            </div>
            </>
          )}

          {(stage === 'reading' || stage === 'importing') && (
            <div className="flex flex-col items-center py-8">
              <Loader2 className="h-8 w-8 text-primary-500 animate-spin mb-3" />
              <p className="text-sm text-gray-700">
                {stage === 'reading' ? 'Reading file…' : 'Importing forms…'}
              </p>
            </div>
          )}

          {stage === 'done' && (
            <div className="flex flex-col items-center py-4 text-center">
              <CheckCircle2 className="h-10 w-10 text-emerald-500 mb-3" />
              <p className="text-base font-semibold text-gray-900">
                Imported {formsImported} form{formsImported === 1 ? '' : 's'} ({variablesImported} variable{variablesImported === 1 ? '' : 's'})
              </p>
              {warnings.length > 0 && (
                <div className="mt-4 w-full text-left p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-xs font-semibold text-amber-800 mb-1">Notes</p>
                  <ul className="text-xs text-amber-700 list-disc pl-4 space-y-0.5">
                    {warnings.map((w, i) => (
                      <li key={i}>{w}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {stage === 'error' && (
            <div className="flex flex-col items-center py-4 text-center">
              <AlertTriangle className="h-10 w-10 text-red-500 mb-3" />
              <p className="text-sm font-medium text-gray-800">Import failed</p>
              <p className="text-xs text-red-600 mt-1">{error}</p>
              {warnings.length > 0 && (
                <div className="mt-4 w-full text-left p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-xs font-semibold text-amber-800 mb-1">Per-row warnings</p>
                  <ul className="text-xs text-amber-700 list-disc pl-4 space-y-0.5">
                    {warnings.map((w, i) => (
                      <li key={i}>{w}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="p-4 border-t border-gray-200 flex justify-end gap-3">
          {stage === 'picking' && (
            <>
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={run}
                disabled={!file}
                className="px-4 py-2 text-sm text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:opacity-50"
              >
                Import
              </button>
            </>
          )}

          {stage === 'done' && (
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-white bg-primary-600 hover:bg-primary-700 rounded-lg"
            >
              Done
            </button>
          )}

          {stage === 'error' && (
            <>
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Close
              </button>
              <button
                onClick={() => {
                  setStage('picking');
                  setError('');
                  setWarnings([]);
                  setFile(null);
                }}
                className="px-4 py-2 text-sm text-white bg-primary-600 hover:bg-primary-700 rounded-lg"
              >
                Try Again
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
